import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Rocket, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

// Product image component with fallback
// Helper to get WebP version of image URL
const getWebpUrl = (src) => {
  if (!src) return '';
  // Replace .jpg or .png with .webp
  return src.replace(/\.(jpg|jpeg|png)$/i, '.webp');
};

const ProductImage = ({ src, alt, className, children }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const getColorFromName = (name) => {
    const colors = [
      'from-pink-400 to-pink-600',
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-purple-400 to-purple-600',
      'from-orange-400 to-orange-600',
      'from-teal-400 to-teal-600',
    ];
    const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (hasError || !src) {
    return (
      <div className={`${className} bg-gradient-to-br ${getColorFromName(alt)} flex items-center justify-center relative`}>
        <div className="text-center text-white p-4">
          <Package className="h-8 w-8 mx-auto mb-2 opacity-80" />
          <p className="text-xs font-medium opacity-90 line-clamp-2">{alt}</p>
        </div>
        {children}
      </div>
    );
  }

  const webpSrc = getWebpUrl(src);

  return (
    <div className={`${className} relative bg-white`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="animate-pulse text-gray-400">
            <Package className="h-6 w-6" />
          </div>
        </div>
      )}
      <picture>
        <source srcSet={webpSrc} type="image/webp" />
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`w-full h-full object-contain ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          onError={() => setHasError(true)}
          onLoad={() => setIsLoading(false)}
        />
      </picture>
      {children}
    </div>
  );
};

// Helper functions for category display
const getCategoryTitle = (cat) => {
  const titles = {
    'drinkware': 'Premium Drinkware',
    'bags': 'Quality Bags',
    'technology': 'Tech Accessories',
    'apparel': 'Branded Apparel',
    'headwear': 'Custom Headwear',
    'writing-instruments': 'Writing Instruments'
  };
  return titles[cat] || cat.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getCategorySubtitle = (cat) => {
  const subtitles = {
    'drinkware': 'Bottles, mugs & tumblers that keep your brand in hand',
    'bags': 'Carry your message everywhere with custom bags',
    'technology': 'Modern tech gifts that impress clients',
    'apparel': 'Wearable branding that makes an impact',
    'headwear': 'Caps, hats & beanies with your logo',
    'writing-instruments': 'Pens & pencils for everyday brand exposure'
  };
  return subtitles[cat] || 'Quality promotional products for your brand';
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Single optimized API call for all homepage data
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/homepage-data`);
        const data = await response.json();
        
        // Process featured slides
        const featured = data.featured_slides.map(slide => ({
          category: slide.category,
          title: getCategoryTitle(slide.category),
          subtitle: getCategorySubtitle(slide.category),
          products: slide.products.map(p => ({
            ...p,
            local_image: p.local_image ? `https://procloudpromo.com${p.local_image}` : ''
          })),
          link: `/products/${slide.category}`
        }));
        setFeaturedProducts(featured);
        
        // Process categories with images
        const categoriesWithImages = data.categories.map(cat => ({
          ...cat,
          image: cat.image ? `https://procloudpromo.com${cat.image}` : ''
        }));
        setCategories(categoriesWithImages);
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (featuredProducts.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [featuredProducts.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredProducts.length) % featuredProducts.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Rocket className="h-16 w-16 mx-auto mb-4 text-pink-600 animate-pulse" />
          <p className="text-xl text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section - Clean Split Design */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-pink-900 min-h-[580px] relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 py-12 relative">
          {featuredProducts.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-8 items-center min-h-[500px]">
              {/* Left side - Text Content */}
              <div className="text-white space-y-6 order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm">
                  <Sparkles className="h-4 w-4 text-pink-400" />
                  <span>Featured Collection</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  {featuredProducts[currentSlide]?.title}
                </h1>
                
                <p className="text-lg md:text-xl text-slate-300 max-w-lg">
                  {featuredProducts[currentSlide]?.subtitle}
                </p>
                
                <div className="flex flex-wrap gap-4 pt-4">
                  <Link to={featuredProducts[currentSlide]?.link || '/products/all'}>
                    <Button size="lg" className="bg-pink-600 hover:bg-pink-700 text-white px-8">
                      Shop Collection
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/products/all">
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                      View All Products
                    </Button>
                  </Link>
                </div>

                {/* Slide indicators */}
                <div className="flex items-center gap-4 pt-6">
                  <button 
                    onClick={prevSlide}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <div className="flex gap-2">
                    {featuredProducts.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === currentSlide
                            ? 'bg-pink-500 w-8'
                            : 'bg-white/30 hover:bg-white/50 w-2'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                  <button 
                    onClick={nextSlide}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    aria-label="Next slide"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Right side - Product Grid */}
              <div className="order-1 lg:order-2">
                <div className="grid grid-cols-2 gap-4">
                  {featuredProducts[currentSlide]?.products.map((product, idx) => (
                    <Link 
                      key={product.product_code} 
                      to={`/product/${product.product_code}`}
                      className={`group ${idx === 0 ? 'col-span-2 row-span-1' : ''}`}
                    >
                      <div className={`bg-white rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-pink-500/20 ${idx === 0 ? 'h-48' : 'h-40'}`}>
                        <div className="relative h-full p-4 flex items-center justify-center">
                          <ProductImage
                            src={product.local_image || product.image_url}
                            alt={product.name}
                            className="h-full w-full object-contain"
                          />
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                            <div className="text-white">
                              <p className="font-semibold text-sm line-clamp-1">{product.name}</p>
                              <p className="text-xs text-pink-300">View Product</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What's Trending */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Shop by Category</h2>
            <p className="text-lg text-gray-600">Explore our most popular promotional product categories</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.slice(0, 8).map((category) => (
              <Link key={category.category_id} to={`/products/${category.category_id}`}>
                <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden h-full">
                  <CardContent className="p-0">
                    <div className="relative aspect-square bg-gray-100">
                      <ProductImage
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <h3 className="font-bold text-lg mb-1 group-hover:text-pink-300 transition-colors">
                          {category.name}
                        </h3>
                        <p className="text-sm text-white/90">
                          {category.product_count} products
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/categories">
              <Button variant="outline" size="lg" data-testid="view-all-categories-btn">
                View All Categories
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose ProCloud?</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-pink-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">1,600+ Products</h3>
              <p className="text-gray-600">Huge range of promotional items to suit every budget and occasion</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-pink-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Custom Branding</h3>
              <p className="text-gray-600">Professional printing and decoration services for your logo</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Rocket className="h-8 w-8 text-pink-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Fast Turnaround</h3>
              <p className="text-gray-600">Quick quotes and reliable delivery to meet your deadlines</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-pink-900 to-pink-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <Rocket className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Launch Your Brand?</h2>
          <p className="text-lg md:text-xl mb-8 text-pink-100 max-w-2xl mx-auto">
            Request a quote for promotional products and let's make your brand soar!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products/all">
              <Button size="lg" className="bg-white text-pink-900 hover:bg-pink-50">
                Browse Products
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-pink-900">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
